import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import AppBar from 'components/app-bar';
import IframeWrapper from 'components/iframe-wrapper';
import Loader from 'components/loader';
import MainWrapper from 'components/main-wrapper';
import i18next from 'i18next';
import ConcreteEnvironment from 'pages/concrete/environment';
import ConcreteFireRating from 'pages/concrete/fire-rating';
import ConcreteProductDetails from 'pages/concrete/product-details';
import ConcreteResults from 'pages/concrete/results';
import ConcreteStructuralElements from 'pages/concrete/structural-elements';
import CountrySelect from 'pages/country-select';
import Home from 'pages/home';
import { HIDDEN_COUNTRIES } from 'pages/country-select/constants';
import PageNotFound from 'pages/page-not-found';
import ProductSelect from 'pages/product-select';
import SteelEnvironment from 'pages/steel/environment';
import SteelFireRating from 'pages/steel/fire-rating';
import SteelProductDetails from 'pages/steel/product-details';
import ProtectedSides from 'pages/steel/protected-sides';
import SteelResults from 'pages/steel/results';
import SteelStructuralElements from 'pages/steel/structural-elements';
import StructureSelect from 'pages/structure-select';
import {
  ROUTES,
  isInIframe,
  ENABLE_SITE_UNDER_MAINTENANCE,
} from 'shared/constants';
import useBeforeUnloadEffect from 'shared/hooks/useBeforeUnloadEffect';
import useScrollToTopEffect from 'shared/hooks/useScrollToTopEffect';
import useLocalStorage from 'shared/hooks/useLocalStorage';
import { SESSION_STORAGE_KEYS } from 'shared/sessionStorageUtils';
import { selectIsQueryError } from 'store/calculator/selectors/calculator';
import { setIsQueryError } from 'store/calculator/slices/calculator';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import SiteUnderMaintenancePage from './pages/site-under-maintenance-page';
import { parseJson } from './shared/utils/jsonUtils';
import { parseQueryParameters } from './shared/utils/parseQueryParameters';

interface DataLayerEvent {
  event: string;
}

declare global {
  interface Window {
    dataLayer: DataLayerEvent[];
    digitalData: object;
  }
}

const AppContent = () => (
  <Routes>
    <Route path={ROUTES.home} element={<Home />} />
    <Route path={ROUTES.countrySelect} element={<CountrySelect />} />
    <Route path={ROUTES.structureSelect} element={<StructureSelect />} />

    <Route path={ROUTES.steel}>
      <Route
        path={ROUTES.structuralElements}
        element={<SteelStructuralElements />}
      />
      <Route path={ROUTES.productSelect} element={<ProductSelect />} />
      <Route path={ROUTES.protectedSides} element={<ProtectedSides />} />
      <Route path={ROUTES.environment} element={<SteelEnvironment />} />
      <Route path={ROUTES.fireRating} element={<SteelFireRating />} />
      <Route path={ROUTES.results} element={<SteelResults />} />
      <Route
        path={`${ROUTES.productDetails}/:productId`}
        element={<SteelProductDetails />}
      />
      <Route path="" element={<PageNotFound />} />
    </Route>
    <Route path={ROUTES.concrete}>
      <Route
        path={ROUTES.structuralElements}
        element={<ConcreteStructuralElements />}
      />
      <Route path={ROUTES.productSelect} element={<ProductSelect />} />
      <Route path={ROUTES.environment} element={<ConcreteEnvironment />} />
      <Route path={ROUTES.fireRating} element={<ConcreteFireRating />} />
      <Route path={ROUTES.results} element={<ConcreteResults />} />
      <Route
        path={`${ROUTES.productDetails}/:productId`}
        element={<ConcreteProductDetails />}
      />
      <Route path="" element={<PageNotFound />} />
    </Route>
    <Route path="*" element={<PageNotFound />} />
  </Routes>
);

const App = ({ isLoading }: { isLoading: boolean }) => {
  const isQueryError = useAppSelector(selectIsQueryError);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { getValueFromLocalStorage, removeStoredValueByKey } = useLocalStorage();

  useEffect(() => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    parseQueryParameters(url);

    const localCountry = getValueFromLocalStorage(SESSION_STORAGE_KEYS.country);

    if (HIDDEN_COUNTRIES.includes(localCountry?.code)) {
      sessionStorage.clear();
      removeStoredValueByKey(SESSION_STORAGE_KEYS.country);
      window.location.replace('/404');
    }
  }, []);

  useEffect(() => {
    const _hsq = ((window as any)._hsq = (window as any)._hsq || []);
    _hsq.push(['setPath', window.location.pathname]);
    _hsq.push(['trackPageView']);

    const updatedDigitalData = {
      'page': {
        'attributes': {
          'EtexDivision': 'etex ebp',
          'fullCanonicalHostname': `${window.location.hostname}`,
          'catalog': `${
            parseJson(localStorage.getItem('country') ?? '')?.code ?? ''
          }`,
          'language': `${i18next.language ?? localStorage.getItem('i18nextLng')}`,
        },
        'pageInfo': {
          'httpStatusCode': '200',
        },
      },
      'user': [
        {
          'profile': [
            {
              'attributes': {
                'ip': '',
              },
            },
          ],
        },
      ],
    };

    window.digitalData = updatedDigitalData;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'digitalData';
    script.innerHTML = `digitalData = ${JSON.stringify(updatedDigitalData)};`;

    const lastScript = document.getElementById('digitalData');
    if (lastScript) {
      lastScript.remove();
    }

    document.head.appendChild(script);
  }, [window.location.pathname]);

  useEffect(() => {
    dispatch(setIsQueryError(false));
  }, [location]);

  useBeforeUnloadEffect();
  useScrollToTopEffect();

  if (isInIframe) {
    return (
      <IframeWrapper>
        <AppContent />
      </IframeWrapper>
    );
  }

  return (
    <>
      {ENABLE_SITE_UNDER_MAINTENANCE === "true" ? (
        <SiteUnderMaintenancePage />
      ) : (
        <>
          <AppBar />
          <MainWrapper>
            {isLoading ? (
              <Loader />
            ) : isQueryError ? (
              <PageNotFound />
            ) : (
              <AppContent />
            )}
          </MainWrapper>
        </>
      )}
    </>
  );
};

export default App;
